import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { toCreasedNormals } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { countEdge, edgeEnds } from './weld';

const EDGE_WIDTH_PX = 1;
// Model edges are sized in device pixels, as a CAD viewport's hairlines are: a
// CSS pixel is two or three of them on a high-DPI screen.
const EDGE_DEVICE_PX = 1.3;
// In a small panel the part is small too, and full-weight edges crowd it.
const SMALL_VIEW_PX = 360;
const LARGE_VIEW_PX = 900;
const SMALL_VIEW_WEIGHT = 0.75;
const MIN_EDGE_CSS_PX = 0.5;
export const FEATURE_ANGLE_DEGREES = 35;
const CREASE_ANGLE = (FEATURE_ANGLE_DEGREES * Math.PI) / 180;
// Creasing hashes every vertex on the main thread; past this a scan-sized STL
// would stall the page for longer than smoother shading is worth.
const MAX_CREASED_TRIANGLES = 600_000;

export type EdgeLines = LineSegments2;

/** Marks what the viewer hangs on a model, so walks over its own meshes skip it. */
export const OVERLAY_KEY = 'viewportOverlay';

export function isOverlay(object: THREE.Object3D): boolean {
  return object.userData[OVERLAY_KEY] === true;
}

export function markOverlay<T extends THREE.Object3D>(object: T): T {
  object.userData[OVERLAY_KEY] = true;
  object.raycast = () => {};
  return object;
}

export function edgeLines(segments: THREE.BufferGeometry, widthPx = EDGE_WIDTH_PX): EdgeLines {
  const positions = segments.getAttribute('position').array as Float32Array;
  const geometry = new LineSegmentsGeometry().setPositions(positions);
  segments.dispose();
  const material = new LineMaterial({ linewidth: widthPx, transparent: true, depthWrite: false });
  return markOverlay(new LineSegments2(geometry, material));
}

const viewport = new THREE.Vector4();

export function edgeWidthFor(pixelRatio: number, shortSidePx: number): number {
  const span = (shortSidePx - SMALL_VIEW_PX) / (LARGE_VIEW_PX - SMALL_VIEW_PX);
  const weight = THREE.MathUtils.lerp(SMALL_VIEW_WEIGHT, 1, THREE.MathUtils.clamp(span, 0, 1));
  return Math.max((EDGE_DEVICE_PX * weight) / Math.max(pixelRatio, 1), MIN_EDGE_CSS_PX);
}

/** Edge lines for a model, which keep a hairline weight on any screen or panel. */
export function modelEdgeLines(segments: THREE.BufferGeometry): EdgeLines {
  const lines = edgeLines(segments);
  lines.onBeforeRender = (renderer) => {
    LineSegments2.prototype.onBeforeRender.call(lines, renderer);
    renderer.getViewport(viewport);
    lines.material.linewidth = edgeWidthFor(renderer.getPixelRatio(), Math.min(viewport.z, viewport.w));
  };
  return lines;
}

export function segmentCount(lines: EdgeLines): number {
  return lines.geometry.getAttribute('instanceStart')?.count ?? 0;
}

export function setEdgeStyle(lines: EdgeLines, color: number, opacity: number): void {
  lines.material.color.setHex(color);
  lines.material.opacity = opacity;
}

export function featureEdges(mesh: THREE.Mesh): THREE.BufferGeometry {
  return new THREE.EdgesGeometry(mesh.geometry, FEATURE_ANGLE_DEGREES);
}

/** Appends the segments only one of `mesh`'s triangles uses, moved by `matrix`. */
export function appendFaceBoundary(mesh: THREE.Mesh, matrix: THREE.Matrix4, out: number[]): void {
  const index = mesh.geometry.index;
  const position = mesh.geometry.getAttribute('position');
  if (!index || !position) return;
  const uses = new Map<number, number>();
  for (let i = 0; i + 2 < index.count; i += 3) {
    const a = index.getX(i);
    const b = index.getX(i + 1);
    const c = index.getX(i + 2);
    countEdge(uses, a, b);
    countEdge(uses, b, c);
    countEdge(uses, c, a);
  }
  const point = new THREE.Vector3();
  uses.forEach((count, key) => {
    if (count !== 1) return;
    for (const vertex of edgeEnds(key)) {
      point.fromBufferAttribute(position, vertex).applyMatrix4(matrix);
      out.push(point.x, point.y, point.z);
    }
  });
}

export function creasedNormals(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  const vertices = geometry.index?.count ?? geometry.getAttribute('position').count;
  if (vertices / 3 > MAX_CREASED_TRIANGLES) return geometry;
  const creased = toCreasedNormals(geometry, CREASE_ANGLE);
  if (creased !== geometry) geometry.dispose();
  return creased;
}
